import React, { useState } from "react";
import PropTypes from "prop-types";
import StatusBadge from "./StatusBadge";
import {
  formatFileSize,
  sanitizeUrl,
  getKnowledgeSourceIcon,
} from "../utils/formatters";
import { formatDate, sanitizeText } from "../utils/sanitize";
import "./KnowledgeSourceItem.css";

const KnowledgeSourceItem = ({
  source,
  onDelete = null,
  onReprocess = null,
  onUpdate = null,
  onLoadContent = null,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);

  const isTextSource = source.type === "text";

  const handleToggleExpand = async () => {
    if (!isTextSource) return;

    if (expanded) {
      // Collapsing — clear any in-progress edit
      setExpanded(false);
      setEditing(false);
      return;
    }

    // Expanding — lazy-load content if not already present
    if (!source.content && onLoadContent) {
      setLoadingContent(true);
      try {
        await onLoadContent(source.id);
      } finally {
        setLoadingContent(false);
      }
    }
    setExpanded(true);
  };

  const handleStartEdit = (e) => {
    e.stopPropagation();
    setEditContent(source.content || "");
    setEditing(true);
    setExpanded(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditContent("");
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!onUpdate || !editContent.trim()) return;

    setSaving(true);
    try {
      await onUpdate(source.id, { content: editContent.trim() });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`ks-item ${isTextSource ? "ks-item-clickable" : ""}`}>
      <div className="ks-icon">{getKnowledgeSourceIcon(source.type)}</div>

      <div className="ks-content">
        <div className="ks-header" onClick={handleToggleExpand}>
          <h4 className="ks-title">
            {source.name || source.sourceUrl || "Untitled"}
          </h4>
          <StatusBadge status={source.status} />
          {isTextSource && (
            <span className="ks-expand-indicator">
              {loadingContent ? "..." : expanded ? "▾" : "▸"}
            </span>
          )}
        </div>

        <div className="ks-meta">
          {source.type === "file" && source.fileSize && (
            <>
              <span>{formatFileSize(source.fileSize)}</span>
              <span>&bull;</span>
            </>
          )}

          {source.type === "url" &&
            source.sourceUrl &&
            sanitizeUrl(source.sourceUrl) && (
              <>
                <a
                  href={sanitizeUrl(source.sourceUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ks-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {source.sourceUrl}
                </a>
                <span>&bull;</span>
              </>
            )}

          <span title={formatDate(source.createdAt)}>
            Added {formatDate(source.createdAt)}
          </span>
        </div>

        {source.errorMessage && (
          <div className="ks-error">{sanitizeText(source.errorMessage)}</div>
        )}

        {isTextSource && expanded && (
          <div className="ks-text-content">
            {editing ? (
              <div className="ks-edit-form">
                <textarea
                  className="ks-edit-textarea"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={8}
                  disabled={saving}
                  maxLength={50000}
                />
                <div className="ks-edit-actions">
                  <button
                    className="ks-btn-text ks-btn-save"
                    onClick={handleSave}
                    disabled={saving || !editContent.trim()}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button
                    className="ks-btn-text ks-btn-cancel"
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="ks-text-preview">
                <pre className="ks-text-body">
                  {source.content || "No content"}
                </pre>
                {onUpdate && (
                  <button
                    className="ks-btn-text ks-btn-edit"
                    onClick={handleStartEdit}
                  >
                    Edit
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ks-actions">
        {source.status === "failed" && onReprocess && (
          <button
            className="ks-btn ks-btn-retry"
            onClick={(e) => {
              e.stopPropagation();
              onReprocess(source.id);
            }}
            title="Retry processing"
            aria-label={`Retry processing ${source.name || "source"}`}
          >
            <span role="img" aria-label="Retry">
              🔄
            </span>
          </button>
        )}

        {onDelete && (
          <button
            className="ks-btn ks-btn-delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(source.id);
            }}
            title="Delete source"
            aria-label={`Delete ${source.name || "source"}`}
          >
            <span role="img" aria-label="Delete">
              🗑️
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

KnowledgeSourceItem.propTypes = {
  source: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.oneOf(["file", "url", "text"]).isRequired,
    name: PropTypes.string,
    sourceUrl: PropTypes.string,
    content: PropTypes.string,
    status: PropTypes.oneOf(["processing", "completed", "failed"]).isRequired,
    fileSize: PropTypes.number,
    createdAt: PropTypes.string,
    errorMessage: PropTypes.string,
  }).isRequired,
  onDelete: PropTypes.func,
  onReprocess: PropTypes.func,
  onUpdate: PropTypes.func,
  onLoadContent: PropTypes.func,
};

export default KnowledgeSourceItem;
